import React, { useEffect, useState } from 'react';
import { FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Chip, Box } from '@mui/material';
import { getLeaveBalances, LeaveBalance } from '../dataProvider';

export interface LeaveTypeSelectorProps {
    value: 'Annual' | 'Unpaid';
    onChange: (value: 'Annual' | 'Unpaid') => void;
    disabled?: boolean;
    employeeId?: string | null;
}

export const LeaveTypeSelector: React.FC<LeaveTypeSelectorProps> = ({
    value,
    onChange,
    disabled = false,
    employeeId = null
}) => {
    const [balances, setBalances] = useState<LeaveBalance[]>([]);

    useEffect(() => {
        const fetchBalances = async () => {
            try {
                const data = await getLeaveBalances(employeeId);
                setBalances(data);
            } catch (error) {
                console.error('Error fetching leave balances:', error);
            }
        };

        fetchBalances();
    }, [employeeId]);

    const getBalance = (leaveType: 'Annual' | 'Unpaid') => {
        const balance = balances.find(b => b.leave_type === leaveType);
        if (!balance) return null;
        const remaining = balance.allocated_days - balance.used_days;
        return { ...balance, remaining };
    };

    const annualBalance = getBalance('Annual');
    const unpaidBalance = getBalance('Unpaid');

    return (
        <FormControl component="fieldset" disabled={disabled}>
            <FormLabel component="legend">Leave Type</FormLabel>
            <RadioGroup
                value={value}
                onChange={(e) => onChange(e.target.value as 'Annual' | 'Unpaid')}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <FormControlLabel value="Annual" control={<Radio />} label="Annual Leave" />
                    {annualBalance && (
                        <Chip
                            label={`${annualBalance.remaining} days remaining`}
                            color={annualBalance.remaining > 0 ? 'success' : 'error'}
                            size="small"
                        />
                    )}
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                    <FormControlLabel value="Unpaid" control={<Radio />} label="Unpaid Leave" />
                    {unpaidBalance && (
                        <Chip
                            label={`${unpaidBalance.used_days} days used`}
                            color="default"
                            size="small"
                        />
                    )}
                </Box>
            </RadioGroup>
        </FormControl>
    );
};
